<template>
  <HeaderComponent/>
  
  <!-- Notifications -->
  <div class="fixed top-[80px] left-[50%] z-[100] translate-x-[-50%] w-[80%] max-w-[400px] flex flex-col">
    <div v-if="errorMessage" class="text-sm w-full rounded-[5px] shadow-4xl text-white bg-red-700 my-[10px] font-bold text-center p-[20px]">
      {{ errorMessage }}
    </div>
    <div v-if="successMessage" class="text-sm w-full rounded-[5px] shadow-4xl text-white bg-green-700 my-[10px] font-bold text-center p-[20px]">
      {{ successMessage }}
    </div>
  </div>

  <!-- Main Content -->
  <section class="w-full md:h-[calc(100vh-70px)] mt-[70px] relative flex justify-center bg-gray-200 items-center">
    <div class="md:flex hidden justify-center items-center w-full h-full absolute">
      <div class="md:w-[60%] w-full h-full"></div>
      <div class="md:w-[40%] w-full h-full bg-white"></div>
    </div>
    
    <div class="container lg:max-w-[1200px] px-[20px] flex md:flex-row flex-col justify-center items-center w-full h-full relative">
      <!-- Left Column - Cart Items -->
      <div class="md:w-[60%] w-full h-full overflow-y-auto parent md:p-[50px] py-[50px]">
        <!-- Cart Section -->
        <div class="w-full relative min-h-[200px] bg-white rounded-[10px] p-[20px]">
          <h1 class="text-xl font-bold text-gray-800 pb-[20px] border-b">Panier</h1>
          
          <!-- Empty Cart -->
          <div v-if="cartStore.cart.length === 0" class="w-full h-[150px] flex justify-center items-center text-sm font-bold text-center text-gray-800">
            Aucun produit sélectionné
            <router-link to="/boutique" class="ml-2 text-orange-500 hover:underline">
              Voir les produits
            </router-link>
          </div>
          
          <!-- Cart Items -->
          <div v-for="(product, index) in cartStore.cart" :key="index" class="border-t border-b pt-[20px] relative">
            <div class="w-full flex relative">
              <!-- Product Image -->
              <div class="w-[50px] h-[60px] min-w-[50px] min-h-[60px] rounded-[5px] shadow-4xl flex justify-center items-center overflow-hidden bg-gray-100">
                <img :src="product.image || '/img/default.png'" :alt="product.name" 
                     class="object-contain w-full h-full" @error="handleImageError">
              </div>
              
              <!-- Product Info -->
              <div class="w-full md:pl-[20px] pl-[10px] relative">
                <div class="flex justify-between items-center w-full">
                  <h1 class="text-sm text-gray-600 font-bold">{{ product.name }}</h1>
                  <p class="text-sm font-bold flex items-end justify-end text-gray-700">
                    {{ formatPrice(product.price * product.quantity) }}
                  </p>
                </div>
                
                <div class="text-[10px] font-semibold text-gray-500 w-full">
                  {{ formatPrice(product.price) }} | <span class="text-green-600">En stock</span>
                </div>
                
                <div v-if="product.capacity || product.color" class="text-xs mb-[10px] text-gray-500 font-semibold flex items-center">
                  <span v-if="product.capacity">Capacité : {{ product.capacity }}</span>
                  <span v-if="product.color" class="ml-2">Couleur : {{ product.color }}</span>
                </div>
              </div>
            </div>
            
            <!-- Quantity Controls -->
            <div class="w-full flex justify-between items-center my-[10px]">
              <div class="p-[5px] font-semibold text-gray-500 text-xs rounded-[5px] border">
                <button @click="decreaseQuantity(index)" class="px-1">-</button>
                <span class="mx-[10px]">{{ product.quantity }}</span>
                <button @click="increaseQuantity(index)" class="px-1">+</button>
              </div>
              
              <button @click="removeProduct(index)" class="text-xs text-gray-400 hover:text-red-500 cursor-pointer">
                <i class="fa-solid fa-trash mr-1"></i>Supprimer
              </button>
            </div>
          </div>
        </div>
        
        <!-- Customer Information -->
        <div class="w-full bg-white rounded-[10px] mt-[20px] p-[20px]">
          <h1 class="text-xl font-bold text-gray-800 pb-[20px] border-b">Vos informations personnelles</h1>
          
          <form class="w-full md:py-[15px] py-[10px] relative">
            <div class="w-full relative md:flex justify-between items-center">
              <div class="md:w-[48%] w-full my-[15px] relative">
                <input v-model="nom" type="text" class="w-full p-[10px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100" placeholder="Nom" required>
              </div>
              <div class="md:w-[48%] w-full my-[15px] relative">
                <input v-model="prenoms" type="text" class="w-full p-[10px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100" placeholder="Prénoms" required>
              </div>
            </div>
            
            <div class="w-full relative my-[15px]">
              <input v-model="whatsapp" type="tel" class="w-full p-[10px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100" placeholder="Numéro WhatsApp" required>
            </div>
            
            <div class="w-full relative my-[15px]">
              <input v-model="email" type="email" class="w-full p-[10px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100" placeholder="Email">
            </div>
            
            <div class="w-full relative my-[15px]">
              <input v-model="adresse" type="text" class="w-full p-[10px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100" placeholder="Adresse de livraison" required>
            </div>
          </form>
        </div>
      </div>
      
      <!-- Right Column - Order Summary -->
      <div class="md:w-[40%] w-full h-full overflow-y-auto parent bg-white md:p-[50px] p-[25px] mb-[20px] md:mb-0 rounded-[10px] md:rounded-none">
        <h1 class="text-gray-800 text-xl mb-[20px] font-bold relative">Résumé d'achat</h1>
        
        <div class="w-full relative">
          <!-- Category Totals -->
          <div v-for="category in filteredCategories" :key="category" 
               class="w-full relative flex justify-between my-[10px] items-center">
            <h1 class="text-xs font-semibold text-gray-500">Produits {{ category }}:</h1>
            <p class="text-sm font-bold text-gray-700">{{ formatPrice(totalByCategory[category]) }}</p>
          </div>
          
          <!-- Total -->
          <div class="w-full relative flex justify-between py-[20px] items-center border-t">
            <div>
              <h1 class="text-sm font-bold text-gray-800 uppercase">Total:</h1>
            </div>
            <p class="text-xl font-bold text-orange-500">{{ formatPrice(subTotal) }}</p>
          </div>
          
          <!-- Free Shipping -->
          <div class="w-full p-[10px] py-[20px] rounded-[5px] bg-orange-100 relative">
            <div class="w-full relative flex justify-between items-center">
              <div>
                <h1 class="text-[10px] font-bold text-gray-500">Livraison gratuite</h1>
                <p class="text-sm font-bold text-gray-700">Calavi - Parakou</p>
              </div>
              <p class="text-xl font-bold text-orange-600">0.00 FCFA</p>
            </div>
          </div>
          
          <!-- WhatsApp Button -->
          <button @click="confirmOrder" 
                  class="w-full mt-[20px] h-[45px] flex justify-center items-center p-[10px] rounded-[5px] bg-green-500 hover:bg-green-600 text-white text-sm font-bold text-center transition-colors">
            Confirmer la commande
          </button>
          
          <p class="mt-3 text-xs text-gray-500 text-center">
            Un conseiller vous contactera pour finaliser votre commande
          </p>
        </div>
      </div>
    </div>
  </section>
  
  <!-- Loading Overlay -->
  <div v-if="isLoading" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center top-[70px]">
    <div class="spinner">
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </div>
  </div>
</template>

<script setup>
import HeaderComponent from '../components/HeaderComponent.vue'
import { ref, computed } from 'vue'
import { useCartStore } from '@/store/useCartStore'
import axios from 'axios'

const cartStore = useCartStore()

// Form data
const nom = ref('')
const prenoms = ref('')
const whatsapp = ref('')
const email = ref('')
const adresse = ref('')
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

// Handle image errors
const handleImageError = (event) => {
  event.target.src = '/img/default.png'
}

// Format price
const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA'
}

// Cart actions
const increaseQuantity = (index) => {
  cartStore.cart[index].quantity += 1
}

const decreaseQuantity = (index) => {
  if (cartStore.cart[index].quantity > 1) {
    cartStore.cart[index].quantity -= 1
  } else {
    removeProduct(index)
  }
}

const removeProduct = (index) => {
  cartStore.cart.splice(index, 1)
}

// Calculate totals by category
const totalByCategory = computed(() => {
  const categories = {
    iOS: 0,
    Android: 0,
    Accessoires: 0,
  };

  cartStore.cart.forEach(product => {
    if (product.category === 'iOS') {
      categories.iOS += product.price * product.quantity;
    } else if (product.category === 'Android') {
      categories.Android += product.price * product.quantity;
    } else if (product.category === 'Accessoires') {
      categories.Accessoires += product.price * product.quantity;
    }
  });

  return categories;
})

// Filter categories with positive totals
const filteredCategories = computed(() => {
  return Object.keys(totalByCategory.value).filter(
    category => totalByCategory.value[category] > 0
  )
})

// Subtotal calculation
const subTotal = computed(() => {
  return Object.values(totalByCategory.value).reduce((sum, amount) => sum + amount, 0)
})

// Confirm order and send to WhatsApp
const confirmOrder = async () => {
  if (!nom.value || !whatsapp.value || !adresse.value) {
    errorMessage.value = 'Veuillez remplir tous les champs obligatoires'
    setTimeout(() => errorMessage.value = '', 3000)
    return
  }

  isLoading.value = true

  try {
    // Create sale record
    const saleData = {
      client: {
        nom: nom.value,
        prenoms: prenoms.value,
        email: email.value,
        whatsapp: whatsapp.value,
        adresse: adresse.value
      },
      produits: cartStore.cart.map(product => ({
        _id: product.id,
        lotName: product.name,
        lotCategorie: product.category,
        photos: product.image,
        imei1: product.imei1,
        imei2: product.imei2,
        capacities: product.capacity ? [product.capacity] : [],
        colors: product.color ? [product.color] : [],
        prix: product.price,
        prix_reduit: product.originalPrice,
        quantity: product.quantity,
        stotal: product.price * product.quantity
      })),
      total: subTotal.value
    }

    // Save sale to database
    await axios.post('/api/online', saleData)
    
    successMessage.value = 'Votre commande a été enregistrée avec succès!'
    setTimeout(() => successMessage.value = '', 3000)
    
    cartStore.clearCart()
    nom.value = ''
    prenoms.value = ''
    whatsapp.value = ''
    email.value = ''
    adresse.value = ''

  } catch (error) {
    errorMessage.value = 'Une erreur est survenue lors de la confirmation de votre commande'
    setTimeout(() => errorMessage.value = '', 3000)
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
/* Vos styles existants restent inchangés */
</style>